import React, { PropsWithChildren, useMemo } from "react";
import UnitsContext, { UnitsContextType } from "./context/UnitsContext";
import backpackSdk from "./global_functions/backpackSdk/sdk";
import { UtilityUnitConversion } from "./global_functions/backpackSdk/utilities/units";
import WarningMessage from "./straps/derived/WarningMessage/WarningMessage";
import { UtilityUnit } from "./types";

const createGetConversionFactor = (
  unitConversions: UtilityUnitConversion[],
  invertedUnitMap: Record<UtilityUnit, number>
) => {
  const unitConversionsMap = unitConversions.reduce(
    (result, { initial_unit_id, target_unit_id, multiplication_factor }) => {
      result[initial_unit_id] = result[initial_unit_id] ?? {};
      result[initial_unit_id]![target_unit_id] = Number(multiplication_factor);
      return result;
    },
    {} as Record<number, Record<number, number>>
  );

  return (
    initialUnit: UtilityUnit | number,
    targetUnit: UtilityUnit | number
  ) => {
    const convertedInitialUnit =
      typeof initialUnit === "number"
        ? initialUnit
        : invertedUnitMap[initialUnit];
    const convertedTargetUnit =
      typeof targetUnit === "number" ? targetUnit : invertedUnitMap[targetUnit];

    return unitConversionsMap?.[convertedInitialUnit]?.[convertedTargetUnit];
  };
};

export default function UnitsContextProvider({
  children,
  fallback,
}: PropsWithChildren<{ readonly fallback?: React.ReactElement }>) {
  const { data: unitsMap, isError: isUnitsError } =
    backpackSdk.utilities.units.useQueryMap();
  const { data: conversions, isError: isConversionsError } =
    backpackSdk.utilities.units.useQueryConversion();

  const unitsContextValue = useMemo<UnitsContextType | undefined>(() => {
    if (unitsMap && conversions) {
      const invertedUnitMap = Object.values(unitsMap).reduce(
        (result, { label, id }) => {
          if (label) {
            result[label] = id;
          }
          return result;
        },
        {} as Record<UtilityUnit, number>
      );

      return {
        isLoading: false,
        unitsMap,
        getConversionFactor: createGetConversionFactor(
          conversions,
          invertedUnitMap
        ),
        getUnitIdFromUnit: (unit) => invertedUnitMap[unit],
      };
    }
  }, [unitsMap, conversions]);

  if (isUnitsError || isConversionsError) {
    return <WarningMessage />;
  }

  if (!unitsContextValue) {
    return fallback ?? null;
  }

  return (
    <UnitsContext.Provider value={unitsContextValue}>
      {children}
    </UnitsContext.Provider>
  );
}
